import React from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "../../lib/withRouter";
import Header2 from "../Header2";
import {
    fetchMemberSummaryByCin,
    generateAndPollMemberSummary,
    getCachedMemberRowByCin,
    normalizeMemberSummaryKind,
    pickMemberSummaryPayload,
    shouldUseMemberSummaryRemote
} from "../../lib/memberSummaryApi";
import { checkMemberEligibility } from "../../lib/eligibilityApi";
import {
    DobWithAgeDisplay,
    buildDemographicsFromLeadRow,
    DEMOGRAPHIC_PLACEHOLDER,
    formatDobWithAge,
    formatGenderDisplay,
    formatLeadListContactValue,
    formatLeadPhoneNumber,
    getLeadFormattedAddressEntries,
    isUnavailableDisplayValue,
    leadSummaryHasRenderableBody
} from "./leadSummaryDynamic";
import { LeadSummaryWorkspace } from "./LeadSummaryWorkspace";
import { getLeadScoreBadgeProps, getMemberStatusBadgeProps } from "./leadScoreBadge";
import {
    getClinicalScoreTone,
    getEligibilityTone
} from "./leadBadgeTones";
import { toTitleCaseLabel } from "./leadSummaryLabels";
import { LeadRefreshIcon } from "./leadRefreshIcon";
import { LeadLoadingSpinner } from "./leadLoadingSpinner";
import "./leadSummaryCritical.css";
import "./MembersSummary.less";

function getCinFromProps(props) {
    return String(props.router?.params?.memberId || "").trim();
}

function getSummaryKindFromProps(props) {
    const loc = props.location || props.router?.location;
    const fromQuery = new URLSearchParams(loc?.search || "").get("view");
    if (fromQuery) {
        return normalizeMemberSummaryKind(fromQuery);
    }
    return normalizeMemberSummaryKind(loc?.state?.summaryKind);
}

const MEMBER_SUMMARY_UNCONFIGURED =
    "Member summary API is not configured (set VITE_MEMBER_SERVICES_BASE).";

/**
 * List row from navigation state or session cache — used for summary availability
 * flags and as a demographics fallback when the summary body is not generated yet.
 */
function resolveLeadListRow(props) {
    const cin = getCinFromProps(props);
    if (!cin) {
        return null;
    }
    const cinUpper = cin.toUpperCase();
    const loc = props.location || props.router?.location;
    const stateRow = loc?.state?.leadListRow;
    if (stateRow && String(stateRow.cin || "").trim().toUpperCase() === cinUpper) {
        return stateRow;
    }
    return getCachedMemberRowByCin(cin);
}

/** Demographics / POF from GET /lead-summary `member_details` only (never leads list). */
function getMemberDetailsFromSummary(apiData) {
    const details = apiData?.member_details;
    if (details && typeof details === "object") {
        return details;
    }
    return null;
}

function toStartCase(value) {
    return String(value || "")
        .toLowerCase()
        .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/** Member demographics: empty / unavailable → "-". */
function displayDemographicValue(label, val) {
    if (isUnavailableDisplayValue(val)) {
        return DEMOGRAPHIC_PLACEHOLDER;
    }
    const text = String(val).trim();
    const displayLabel = toTitleCaseLabel(label);
    if (displayLabel === "Phone") {
        const formatted = formatLeadPhoneNumber(text);
        return formatted || DEMOGRAPHIC_PLACEHOLDER;
    }
    if (displayLabel === "Gender") {
        return formatGenderDisplay(text);
    }
    if (
        displayLabel === "Name" ||
        displayLabel === "Language" ||
        displayLabel === "Preferred Language" ||
        displayLabel === "Address"
    ) {
        return toStartCase(text);
    }
    return text;
}

/** POF labels from summary `member_details.pof` (`pof` is string[] or occasional string). */
function normalizePofList(row) {
    const raw = row?.pof;
    if (raw == null) return [];
    if (Array.isArray(raw)) {
        return raw.map((x) => String(x || "").trim()).filter(Boolean);
    }
    if (typeof raw === "string") {
        const t = raw.trim();
        return t ? [t] : [];
    }
    return [];
}

/** Critical flag labels from summary `lcm_summary["Critical Flags"]` (string[]). */
function normalizeCriticalFlagsList(summaryPayload) {
    const raw = summaryPayload?.["Critical Flags"];
    if (raw == null) return [];
    if (Array.isArray(raw)) {
        return raw.map((x) => String(x || "").trim()).filter(Boolean);
    }
    if (typeof raw === "string") {
        const t = raw.trim();
        return t ? [t] : [];
    }
    return [];
}

function computeChipFitCount(list, row, measure, chipAttr, moreAttr) {
    if (!list.length) {
        return 0;
    }
    if (!row || !measure) {
        return null;
    }

    const available = row.clientWidth;
    if (available <= 0) {
        return null;
    }

    const gap = 8;
    const chipEls = measure.querySelectorAll(`[${chipAttr}="chip"]`);
    const chipWidths = Array.from(chipEls).map((el) => el.getBoundingClientRect().width);
    if (chipWidths.length === 0) {
        return null;
    }

    const moreWidthFor = (hidden) => {
        const moreEl = measure.querySelector(`[${moreAttr}="${hidden}"]`);
        if (moreEl) {
            return moreEl.getBoundingClientRect().width;
        }
        return 72;
    };

    let best = 0;
    for (let count = Math.min(list.length, chipWidths.length); count >= 0; count -= 1) {
        const hidden = list.length - count;
        let total = 0;
        for (let i = 0; i < count; i += 1) {
            total += chipWidths[i] + (i > 0 ? gap : 0);
        }
        if (hidden > 0) {
            total += (count > 0 ? gap : 0) + moreWidthFor(hidden);
        }
        if (total <= available + 0.5) {
            best = count;
            break;
        }
    }

    return best;
}

/** Preferred language values (`preferred_language` is string[] or string, possibly delimited). */
function normalizeLanguageList(value) {
    if (value == null) return [];
    if (Array.isArray(value)) {
        return value
            .map((x) => String(x || "").trim())
            .filter((x) => x && !isUnavailableDisplayValue(x))
            .map((x) => toStartCase(x));
    }
    if (typeof value === "string") {
        const t = value.trim();
        if (!t || isUnavailableDisplayValue(t)) return [];
        const parts = t
            .split(/\s+and\s+|[\n;,|]+/i)
            .map((p) => p.trim())
            .filter(Boolean);
        if (parts.length > 1) {
            return parts.map((p) => toStartCase(p));
        }
        return [toStartCase(t)];
    }
    return [];
}

/** Soft upper bound while measuring; actual visible count is fit-to-width. */
const MAX_VISIBLE_POF = 24;

function getEligibilityLabel(row) {
    const raw = String(
        row?.waystar_authorization ?? row?.eligibility ?? row?.eligibility_status ?? ""
    ).trim();
    if (!raw) {
        return DEMOGRAPHIC_PLACEHOLDER;
    }
    if (/^(not\s+available|unavailable)$/i.test(raw)) {
        return "Unavailable";
    }
    return toStartCase(raw);
}

function getClinicalScoreDisplay(row) {
    const raw =
        row?.lead_score ??
        row?.patient_clinical_score ??
        row?.clinical_score ??
        row?.patientClinicalScore ??
        "";
    if (isUnavailableDisplayValue(raw)) {
        return { label: DEMOGRAPHIC_PLACEHOLDER, tier: "unknown" };
    }
    const { label, tier } = getLeadScoreBadgeProps(raw, { displayCanonical: true });
    return {
        label: isUnavailableDisplayValue(label) ? DEMOGRAPHIC_PLACEHOLDER : label,
        tier: tier === "high" || tier === "mid" || tier === "low" ? tier : "unknown"
    };
}

function formatAddressInline(entry) {
    return String(entry || "")
        .replace(/\n+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function FeedbackIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
                d="M5.5 4.5H18.5C19.6046 4.5 20.5 5.39543 20.5 6.5V13.5C20.5 14.6046 19.6046 15.5 18.5 15.5H11.2L7.2 19.25V15.5H5.5C4.39543 15.5 3.5 14.6046 3.5 13.5V6.5C3.5 5.39543 4.39543 4.5 5.5 4.5Z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
            />
            <circle cx="8.5" cy="10" r="1.15" fill="currentColor" />
            <circle cx="12" cy="10" r="1.15" fill="currentColor" />
            <circle cx="15.5" cy="10" r="1.15" fill="currentColor" />
        </svg>
    );
}

function MemberBackArrow() {
    return (
        <span className="lead-member-hero__back-icon" aria-hidden="true">
            <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                focusable="false"
            >
                <path
                    d="M19 12H5M12 5l-7 7 7 7"
                    stroke="#5F7470"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    );
}

function MemberAvatar({ className = "" }) {
    return (
        <svg
            className={className || "lead-member-hero__avatar"}
            width="36"
            height="36"
            viewBox="0 0 36 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            focusable="false"
            aria-hidden="true"
            role="img"
        >
            <path
                d="M18 0C8.064 0 0 8.064 0 18C0 27.936 8.064 36 18 36C27.936 36 36 27.936 36 18C36 8.064 27.936 0 18 0ZM18 7.2C21.474 7.2 24.3 10.026 24.3 13.5C24.3 16.974 21.474 19.8 18 19.8C14.526 19.8 11.7 16.974 11.7 13.5C11.7 10.026 14.526 7.2 18 7.2ZM18 32.4C14.346 32.4 10.026 30.924 6.948 27.216C10.1013 24.7432 13.9928 23.3993 18 23.3993C22.0072 23.3993 25.8987 24.7432 29.052 27.216C25.974 30.924 21.654 32.4 18 32.4Z"
                fill="#CFA66B"
            />
        </svg>
    );
}

MemberAvatar.propTypes = {
    className: PropTypes.string
};

export class MembersMemberDetail extends React.Component {
    static propTypes = {
        query: PropTypes.object,
        settings: PropTypes.object,
        location: PropTypes.object,
        urlParams: PropTypes.object,
        router: PropTypes.object
    };

    constructor(props) {
        super(props);
        this._toastTimer = null;
        this.pofChipsRef = null;
        this.pofMeasureRef = null;
        this._pofResizeObserver = null;
        this.criticalFlagsChipsRef = null;
        this.criticalFlagsMeasureRef = null;
        this._criticalFlagsResizeObserver = null;
        this.state = {
            isLoading: true,
            apiData: null,
            leadListRow: null,
            error: null,
            summaryMissing: false,
            cinNotFound: false,
            isRefreshingSummary: false,
            isCheckingEligibility: false,
            toast: null,
            addressModalOpen: false,
            phoneModalOpen: false,
            languageModalOpen: false,
            pofModalOpen: false,
            criticalFlagsModalOpen: false,
            visiblePofCount: null,
            visibleCriticalFlagsCount: null
        };
    }

    componentDidMount() {
        const query = new URLSearchParams(this.props.location.search);
        const _tab = query.get("_tab");
        if (!_tab || _tab !== "membersSummary") {
            const cin = getCinFromProps(this.props);
            const loc = this.props.location || this.props.router?.location;
            query.set("_tab", "membersSummary");
            if (!query.get("view")) {
                query.set("view", getSummaryKindFromProps(this.props));
            }
            this.props.router.navigate(
                {
                    pathname: `/members-summary/member/${encodeURIComponent(cin)}`,
                    search: `?${query.toString()}`
                },
                { replace: true, state: loc?.state }
            );
        }

        window.addEventListener("resize", this.handleHeroChipFitCheck);

        if (!shouldUseMemberSummaryRemote()) {
            this.setState({
                isLoading: false,
                error: MEMBER_SUMMARY_UNCONFIGURED,
                apiData: null
            });
            return;
        }
        this.loadFromApi();
    }

    componentDidUpdate(prevProps, prevState) {
        const wasOpen =
            prevState.addressModalOpen ||
            prevState.phoneModalOpen ||
            prevState.languageModalOpen ||
            prevState.pofModalOpen ||
            prevState.criticalFlagsModalOpen;
        const isOpen =
            this.state.addressModalOpen ||
            this.state.phoneModalOpen ||
            this.state.languageModalOpen ||
            this.state.pofModalOpen ||
            this.state.criticalFlagsModalOpen;
        if (!wasOpen && isOpen) {
            document.addEventListener("keydown", this.handleModalKeyDown);
            document.body.style.overflow = "hidden";
        } else if (wasOpen && !isOpen) {
            document.removeEventListener("keydown", this.handleModalKeyDown);
            document.body.style.overflow = "";
        }

        if (
            prevState.apiData !== this.state.apiData ||
            prevState.isLoading !== this.state.isLoading
        ) {
            if (prevState.apiData !== this.state.apiData) {
                this.setState(
                    { visiblePofCount: null, visibleCriticalFlagsCount: null },
                    () => {
                        this.scheduleHeroChipFitCheck();
                    }
                );
            } else {
                this.scheduleHeroChipFitCheck();
            }
        }

        const prevCin = getCinFromProps(prevProps);
        const cin = getCinFromProps(this.props);
        const prevKind = getSummaryKindFromProps(prevProps);
        const kind = getSummaryKindFromProps(this.props);
        if (prevCin === cin && prevKind === kind) return;

        if (!shouldUseMemberSummaryRemote()) {
            this.setState({
                isLoading: false,
                error: MEMBER_SUMMARY_UNCONFIGURED,
                apiData: null
            });
            return;
        }
        this.loadFromApi();
    }

    componentWillUnmount() {
        if (this._toastTimer) {
            clearTimeout(this._toastTimer);
            this._toastTimer = null;
        }
        if (this._pofFitRaf) {
            cancelAnimationFrame(this._pofFitRaf);
            this._pofFitRaf = null;
        }
        if (this._criticalFlagsFitRaf) {
            cancelAnimationFrame(this._criticalFlagsFitRaf);
            this._criticalFlagsFitRaf = null;
        }
        if (this._pofResizeObserver) {
            this._pofResizeObserver.disconnect();
            this._pofResizeObserver = null;
        }
        if (this._criticalFlagsResizeObserver) {
            this._criticalFlagsResizeObserver.disconnect();
            this._criticalFlagsResizeObserver = null;
        }
        window.removeEventListener("resize", this.handleHeroChipFitCheck);
        document.removeEventListener("keydown", this.handleModalKeyDown);
        document.body.style.overflow = "";
    }

    setPofChipsRef = (node) => {
        this.pofChipsRef = node;
        if (this._pofResizeObserver) {
            this._pofResizeObserver.disconnect();
            this._pofResizeObserver = null;
        }
        if (node && typeof ResizeObserver !== "undefined") {
            this._pofResizeObserver = new ResizeObserver(() => {
                this.scheduleHeroChipFitCheck();
            });
            this._pofResizeObserver.observe(node);
        }
        this.scheduleHeroChipFitCheck();
    };

    setPofMeasureRef = (node) => {
        this.pofMeasureRef = node;
        this.scheduleHeroChipFitCheck();
    };

    setCriticalFlagsChipsRef = (node) => {
        this.criticalFlagsChipsRef = node;
        if (this._criticalFlagsResizeObserver) {
            this._criticalFlagsResizeObserver.disconnect();
            this._criticalFlagsResizeObserver = null;
        }
        if (node && typeof ResizeObserver !== "undefined") {
            this._criticalFlagsResizeObserver = new ResizeObserver(() => {
                this.scheduleHeroChipFitCheck();
            });
            this._criticalFlagsResizeObserver.observe(node);
        }
        this.scheduleHeroChipFitCheck();
    };

    setCriticalFlagsMeasureRef = (node) => {
        this.criticalFlagsMeasureRef = node;
        this.scheduleHeroChipFitCheck();
    };

    handleHeroChipFitCheck = () => {
        this.scheduleHeroChipFitCheck();
    };

    scheduleHeroChipFitCheck = () => {
        if (this._pofFitRaf) {
            cancelAnimationFrame(this._pofFitRaf);
        }
        this._pofFitRaf = requestAnimationFrame(() => {
            this._pofFitRaf = null;
            this.updatePofFitState();
            this.updateCriticalFlagsFitState();
        });
    };

    updatePofFitState = () => {
        const pofList = normalizePofList(getMemberDetailsFromSummary(this.state.apiData));
        const best = computeChipFitCount(
            pofList,
            this.pofChipsRef,
            this.pofMeasureRef,
            "data-pof-measure",
            "data-pof-measure-more"
        );
        if (best === null) {
            return;
        }
        if (best !== this.state.visiblePofCount) {
            this.setState({ visiblePofCount: best });
        }
    };

    updateCriticalFlagsFitState = () => {
        const kind = getSummaryKindFromProps(this.props);
        const ls = pickMemberSummaryPayload(this.state.apiData, kind);
        const criticalFlagsList = normalizeCriticalFlagsList(ls);
        const best = computeChipFitCount(
            criticalFlagsList,
            this.criticalFlagsChipsRef,
            this.criticalFlagsMeasureRef,
            "data-critical-flags-measure",
            "data-critical-flags-measure-more"
        );
        if (best === null) {
            return;
        }
        if (best !== this.state.visibleCriticalFlagsCount) {
            this.setState({ visibleCriticalFlagsCount: best });
        }
    };

    handleModalKeyDown = (event) => {
        if (event.key !== "Escape") {
            return;
        }
        if (this.state.addressModalOpen) {
            this.closeAddressModal();
        } else if (this.state.phoneModalOpen) {
            this.closePhoneModal();
        } else if (this.state.languageModalOpen) {
            this.closeLanguageModal();
        } else if (this.state.pofModalOpen) {
            this.closePofModal();
        } else if (this.state.criticalFlagsModalOpen) {
            this.closeCriticalFlagsModal();
        }
    };

    loadFromApi = () => {
        const cin = getCinFromProps(this.props);
        const kind = getSummaryKindFromProps(this.props);
        if (!cin) {
            this.setState({ isLoading: false, apiData: null, leadListRow: null, error: null });
            return;
        }

        this.setState({
            isLoading: true,
            error: null,
            summaryMissing: false,
            cinNotFound: false
        });

        const resolveRowThenSummary = async () => {
            // Nav/cache only — demographics/POF come from summary `member_details` after GET.
            const leadListRow = resolveLeadListRow(this.props);

            if (!shouldUseMemberSummaryRemote()) {
                return {
                    apiData: null,
                    leadListRow,
                    cinNotFound: false,
                    summaryMissing: false,
                    error: MEMBER_SUMMARY_UNCONFIGURED
                };
            }

            // List flags are authoritative for summary availability.
            const summaryPosted =
                kind === "rn"
                    ? leadListRow?.rn_summary_posted
                    : leadListRow?.lcm_summary_posted;
            if (leadListRow && summaryPosted !== true) {
                return {
                    apiData: null,
                    leadListRow,
                    cinNotFound: false,
                    summaryMissing: true,
                    error: null
                };
            }

            try {
                const apiData = await fetchMemberSummaryByCin(cin, kind);
                return {
                    apiData,
                    leadListRow,
                    cinNotFound: false,
                    summaryMissing: false,
                    error: null
                };
            } catch (err) {
                if (err?.status === 404 || err?.status === 410) {
                    if (leadListRow) {
                        return {
                            apiData: null,
                            leadListRow,
                            cinNotFound: false,
                            summaryMissing: true,
                            error: null
                        };
                    }
                    return {
                        apiData: null,
                        leadListRow: null,
                        cinNotFound: true,
                        summaryMissing: false,
                        error: null
                    };
                }
                return {
                    apiData: null,
                    leadListRow,
                    cinNotFound: false,
                    summaryMissing: false,
                    error: err?.message || String(err)
                };
            }
        };

        resolveRowThenSummary()
            .then((result) => {
                if (getCinFromProps(this.props) !== cin) {
                    return;
                }
                if (getSummaryKindFromProps(this.props) !== kind) {
                    return;
                }
                this.setState({
                    apiData: result.apiData,
                    leadListRow: result.leadListRow,
                    summaryMissing: Boolean(result.summaryMissing),
                    cinNotFound: Boolean(result.cinNotFound),
                    error: result.error,
                    isLoading: false
                });
            })
            .catch((err) => {
                if (getCinFromProps(this.props) !== cin) {
                    return;
                }
                if (getSummaryKindFromProps(this.props) !== kind) {
                    return;
                }
                this.setState({
                    error: err?.message || String(err),
                    isLoading: false,
                    apiData: null,
                    leadListRow: null,
                    summaryMissing: false,
                    cinNotFound: false
                });
            });
    };

    showToast = (message, _variant = "success", options = {}) => {
        if (this._toastTimer) {
            clearTimeout(this._toastTimer);
            this._toastTimer = null;
        }
        this.setState({
            toast: {
                message: message || "",
                title: options.title || "",
                detail: options.detail || ""
            }
        });
        this._toastTimer = setTimeout(() => {
            this._toastTimer = null;
            this.setState({ toast: null });
        }, 4000);
    };

    dismissToast = () => {
        if (this._toastTimer) {
            clearTimeout(this._toastTimer);
            this._toastTimer = null;
        }
        this.setState({ toast: null });
    };

    refreshSummary = async () => {
        const cin = getCinFromProps(this.props);
        const kind = getSummaryKindFromProps(this.props);
        if (!cin) return;
        if (this.state.isRefreshingSummary) return;

        // Async: hold the spinner until the worker actually finishes (poll the on_demand
        // block), then show the REAL outcome — never a premature "refreshed".
        this.setState({ isRefreshingSummary: true });
        try {
            const { status, apiData, failed } =
                await generateAndPollMemberSummary(cin, kind);

            const stillHere =
                getCinFromProps(this.props) === cin &&
                getSummaryKindFromProps(this.props) === kind;
            if (stillHere && !failed) {
                this.setState({ apiData, error: null, isRefreshingSummary: false });
            } else {
                this.setState({ isRefreshingSummary: false });
            }

            if (failed) {
                this.showToast("Failed to refresh summary", "error");
            } else if (status === "unchanged") {
                this.showToast("Summary Unchanged", "warning");
            } else {
                this.showToast("Summary Refreshed", "success");
            }
        } catch (err) {
            this.setState({ isRefreshingSummary: false });
            this.showToast(err?.message || "Failed to refresh summary", "error");
        }
    };

    generateMissingSummary = async () => {
        const cin = getCinFromProps(this.props);
        const kind = getSummaryKindFromProps(this.props);
        if (!cin || this.state.isRefreshingSummary) return;

        this.setState({ isRefreshingSummary: true });
        try {
            const { apiData, failed } =
                await generateAndPollMemberSummary(cin, kind);
            if (
                getCinFromProps(this.props) !== cin ||
                getSummaryKindFromProps(this.props) !== kind
            ) {
                this.setState({ isRefreshingSummary: false });
                return;
            }
            if (failed) {
                this.setState({ isRefreshingSummary: false });
                this.showToast("Failed to generate summary", "error");
                return;
            }
            this.setState({
                apiData,
                summaryMissing: false,
                error: null,
                isRefreshingSummary: false
            });
            this.showToast("Summary Generated", "success");
        } catch (err) {
            if (
                getCinFromProps(this.props) === cin &&
                getSummaryKindFromProps(this.props) === kind
            ) {
                this.setState({ isRefreshingSummary: false });
                this.showToast(err?.message || "Failed to generate summary", "error");
            }
        }
    };

    verifyEligibility = async () => {
        const cin = getCinFromProps(this.props);
        const { apiData, isCheckingEligibility } = this.state;
        const memberRow = getMemberDetailsFromSummary(apiData);
        if (!cin || !memberRow || isCheckingEligibility) return;

        this.setState({ isCheckingEligibility: true });
        try {
            const result = await checkMemberEligibility(memberRow, cin);
            if (getCinFromProps(this.props) !== cin) {
                return;
            }
            const status = String(result?.status || "").trim() || "Unavailable";
            const empiId = String(result?.empiId || "").trim();
            const authPatch = {
                waystar_authorization: status,
                ...(empiId
                    ? {
                          empiId,
                          empi_id: empiId
                      }
                    : {})
            };
            this.setState((prev) => {
                if (!prev.apiData?.member_details) {
                    return { isCheckingEligibility: false };
                }
                return {
                    isCheckingEligibility: false,
                    apiData: {
                        ...prev.apiData,
                        member_details: {
                            ...prev.apiData.member_details,
                            ...authPatch
                        }
                    }
                };
            });
            const isRejected = status.toLowerCase() === "rejected";
            this.showToast(status, isRejected ? "error" : "success");
        } catch (err) {
            if (getCinFromProps(this.props) === cin) {
                this.setState({ isCheckingEligibility: false });
                this.showToast(err?.message || "Failed to verify eligibility", "error");
            }
        }
    };

    goBack = () => {
        const loc = this.props.location || this.props.router?.location;
        const leadsListRestore = loc?.state?.leadsListRestore || null;
        this.props.router.navigate(
            {
                pathname: "/members-summary",
                search: "?_tab=membersSummary"
            },
            leadsListRestore ? { state: { leadsListRestore } } : undefined
        );
    };

    renderToast() {
        const toast = this.state.toast;
        if (!toast) return null;

        const parts = [toast.title, toast.message, toast.detail]
            .map((part) => String(part || "").trim())
            .filter(Boolean);
        const text = parts.join(" — ");
        if (!text) return null;

        return createPortal(
            <div className="lead-toast" role="status" aria-live="polite">
                <p className="lead-toast__message">{text}</p>
                <button
                    type="button"
                    className="lead-toast__close"
                    onClick={this.dismissToast}
                    aria-label="Close notification"
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <circle cx="10" cy="10" r="9" stroke="#293C38" strokeWidth="2" />
                        <path
                            d="M7 7l6 6M13 7l-6 6"
                            stroke="#293C38"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    </svg>
                </button>
            </div>,
            document.body
        );
    }

    openAddressModal = () => {
        this.setState({ addressModalOpen: true });
    };

    closeAddressModal = () => {
        this.setState({ addressModalOpen: false });
    };

    openPhoneModal = () => {
        this.setState({ phoneModalOpen: true });
    };

    closePhoneModal = () => {
        this.setState({ phoneModalOpen: false });
    };

    openLanguageModal = () => {
        this.setState({ languageModalOpen: true });
    };

    closeLanguageModal = () => {
        this.setState({ languageModalOpen: false });
    };

    openPofModal = () => {
        this.setState({ pofModalOpen: true });
    };

    closePofModal = () => {
        this.setState({ pofModalOpen: false });
    };

    openCriticalFlagsModal = () => {
        this.setState({ criticalFlagsModalOpen: true });
    };

    closeCriticalFlagsModal = () => {
        this.setState({ criticalFlagsModalOpen: false });
    };

    renderModalShell({ title, count, onClose, children, labelledBy }) {
        const overlay = (
            <div className="lead-modal-overlay" role="presentation" onClick={onClose}>
                <div
                    className="lead-modal lead-modal--popup"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={labelledBy}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="lead-modal__header">
                        <div className="lead-modal__title-row">
                            <h2 id={labelledBy} className="lead-modal__title">
                                {title}
                            </h2>
                            {count != null ? (
                                <span className="lead-modal__count" aria-label={`${count} items`}>
                                    {count}
                                </span>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            className="lead-modal__close"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            <i className="fa fa-times-circle" aria-hidden="true" />
                        </button>
                    </div>
                    <div className="lead-modal__body">{children}</div>
                </div>
            </div>
        );

        if (typeof document === "undefined" || !document.body) {
            return overlay;
        }
        return createPortal(overlay, document.body);
    }

    renderAddressModal(addresses) {
        if (!this.state.addressModalOpen) {
            return null;
        }
        return this.renderModalShell({
            title: "Addresses",
            count: addresses.length,
            onClose: this.closeAddressModal,
            labelledBy: "lead-address-modal-title",
            children: (
                <ul className="lead-modal-address-list">
                    {addresses.map((entry, index) => (
                        <li key={`addr-${index}`} className="lead-modal-address-item">
                            {toStartCase(formatAddressInline(entry))}
                        </li>
                    ))}
                </ul>
            )
        });
    }

    renderPhoneModal(phones) {
        if (!this.state.phoneModalOpen) {
            return null;
        }
        return this.renderModalShell({
            title: "Phone Numbers",
            count: phones.length,
            onClose: this.closePhoneModal,
            labelledBy: "lead-phone-modal-title",
            children: (
                <ul className="lead-modal-address-list">
                    {phones.map((entry, index) => (
                        <li key={`phone-modal-${index}`} className="lead-modal-address-item">
                            {entry}
                        </li>
                    ))}
                </ul>
            )
        });
    }

    renderLanguageModal(languages) {
        if (!this.state.languageModalOpen) {
            return null;
        }
        return this.renderModalShell({
            title: "Languages",
            count: languages.length,
            onClose: this.closeLanguageModal,
            labelledBy: "lead-language-modal-title",
            children: (
                <ul className="lead-modal-address-list">
                    {languages.map((entry, index) => (
                        <li key={`lang-modal-${index}`} className="lead-modal-address-item">
                            {entry}
                        </li>
                    ))}
                </ul>
            )
        });
    }

    renderPofModal(pofList) {
        if (!this.state.pofModalOpen) {
            return null;
        }
        return this.renderModalShell({
            title: "Population of Focus",
            count: pofList.length,
            onClose: this.closePofModal,
            labelledBy: "lead-pof-modal-title",
            children: (
                <div className="lead-modal-pof-grid">
                    {pofList.map((label, idx) => (
                        <span key={`pof-modal-${idx}`} className="lead-pof-chip">
                            {label}
                        </span>
                    ))}
                </div>
            )
        });
    }

    renderCriticalFlagsModal(criticalFlagsList) {
        if (!this.state.criticalFlagsModalOpen) {
            return null;
        }
        return this.renderModalShell({
            title: "Critical Flags",
            count: criticalFlagsList.length,
            onClose: this.closeCriticalFlagsModal,
            labelledBy: "lead-critical-flags-modal-title",
            children: (
                <div className="lead-modal-pof-grid">
                    {criticalFlagsList.map((label, idx) => (
                        <span key={`critical-flags-modal-${idx}`} className="lead-pof-chip">
                            {label}
                        </span>
                    ))}
                </div>
            )
        });
    }

    renderHeroChipSection({
        label,
        chipList,
        blankShell,
        visibleCount,
        chipsRef,
        measureRef,
        onOpenModal,
        chipMeasureAttr,
        moreMeasureAttr,
        measureKey
    }) {
        const isEmpty = blankShell || chipList.length === 0;
        const fittedCount = blankShell
            ? 0
            : visibleCount == null
              ? Math.min(chipList.length, MAX_VISIBLE_POF)
              : Math.min(visibleCount, chipList.length);
        const visibleChips = blankShell ? [] : chipList.slice(0, fittedCount);
        const hiddenCount = blankShell ? 0 : Math.max(0, chipList.length - visibleChips.length);

        return (
            <div className="lead-member-hero__pof-col">
                <div className="lead-member-hero__pof">
                    <p
                        className={`lead-member-hero__pof-label${
                            isEmpty ? " lead-member-hero__pof-label--empty" : ""
                        }`}
                    >
                        {label}
                    </p>
                    {isEmpty ? (
                        <p className="lead-member-hero__pof-empty">{DEMOGRAPHIC_PLACEHOLDER}</p>
                    ) : (
                        <>
                            <div className="lead-member-hero__pof-chips" ref={chipsRef}>
                                {visibleChips.map((chipLabel, idx) => (
                                    <span key={`${measureKey}-${idx}`} className="lead-pof-chip">
                                        {chipLabel}
                                    </span>
                                ))}
                                {hiddenCount > 0 ? (
                                    <button
                                        type="button"
                                        className="lead-pof-chip lead-pof-chip--more"
                                        onClick={onOpenModal}
                                    >
                                        + {hiddenCount} more
                                    </button>
                                ) : null}
                            </div>
                            <div
                                className="lead-member-hero__pof-chips-measure"
                                ref={measureRef}
                                aria-hidden="true"
                            >
                                {chipList.map((chipLabel, idx) => (
                                    <span
                                        key={`${measureKey}-measure-${idx}`}
                                        className="lead-pof-chip"
                                        {...{ [chipMeasureAttr]: "chip" }}
                                    >
                                        {chipLabel}
                                    </span>
                                ))}
                                {chipList.map((_, idx) => {
                                    const hidden = chipList.length - idx;
                                    if (hidden <= 0) {
                                        return null;
                                    }
                                    return (
                                        <span
                                            key={`${measureKey}-measure-more-${hidden}`}
                                            className="lead-pof-chip lead-pof-chip--more"
                                            {...{ [moreMeasureAttr]: hidden }}
                                        >
                                            + {hidden} more
                                        </span>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    renderMemberHeroCard(memberDetails, cin, pofList, options = {}) {
        const blankShell = Boolean(options.blankShell);
        const criticalFlagsList = Array.isArray(options.criticalFlagsList)
            ? options.criticalFlagsList
            : [];
        const { isCheckingEligibility, visiblePofCount, visibleCriticalFlagsCount } = this.state;
        const { name } = buildDemographicsFromLeadRow(memberDetails || {}, cin);
        const displayName = blankShell
            ? DEMOGRAPHIC_PLACEHOLDER
            : displayDemographicValue("Name", name) || DEMOGRAPHIC_PLACEHOLDER;
        const cinText = String(cin || "").trim();
        const phoneRaw = formatLeadPhoneNumber(memberDetails?.phone);
        const emailRaw = formatLeadListContactValue(memberDetails?.email);
        const phoneLines = blankShell
            ? []
            : String(phoneRaw || "")
                  .split("\n")
                  .map((line) => line.trim())
                  .filter(Boolean);
        const primaryPhone = blankShell
            ? DEMOGRAPHIC_PLACEHOLDER
            : phoneLines.length > 0
              ? phoneLines[0]
              : DEMOGRAPHIC_PLACEHOLDER;
        const email = blankShell
            ? DEMOGRAPHIC_PLACEHOLDER
            : emailRaw
              ? displayDemographicValue("Email", emailRaw)
              : DEMOGRAPHIC_PLACEHOLDER;
        const gender = blankShell
            ? DEMOGRAPHIC_PLACEHOLDER
            : displayDemographicValue("Gender", memberDetails?.gender);
        const languageEntries = blankShell
            ? []
            : normalizeLanguageList(memberDetails?.preferred_language);
        const preferredLanguage = blankShell
            ? DEMOGRAPHIC_PLACEHOLDER
            : languageEntries.length > 0
              ? languageEntries[0]
              : DEMOGRAPHIC_PLACEHOLDER;
        const addressEntries = blankShell
            ? []
            : getLeadFormattedAddressEntries(memberDetails?.address);
        const primaryAddress = blankShell
            ? DEMOGRAPHIC_PLACEHOLDER
            : addressEntries.length
              ? toStartCase(formatAddressInline(addressEntries[0]))
              : DEMOGRAPHIC_PLACEHOLDER;
        const hasDobAge =
            !blankShell && Boolean(formatDobWithAge(memberDetails?.dob, memberDetails?.age));
        const eligibilityLabel = blankShell
            ? DEMOGRAPHIC_PLACEHOLDER
            : getEligibilityLabel(memberDetails);
        const clinicalScore = blankShell
            ? { label: DEMOGRAPHIC_PLACEHOLDER, tier: "unknown" }
            : getClinicalScoreDisplay(memberDetails);
        const clinicalTone = getClinicalScoreTone(clinicalScore.tier || clinicalScore.label);
        const memberStatusBadge = blankShell
            ? { label: DEMOGRAPHIC_PLACEHOLDER, tone: "grey" }
            : (() => {
                  const badge = getMemberStatusBadgeProps(memberDetails?.member_status);
                  const tone =
                      badge.tier === "low"
                          ? "green"
                          : badge.tier === "mid"
                            ? "orange"
                            : "grey";
                  return { label: badge.label, tone };
              })();
        const eligibilityTone = getEligibilityTone(eligibilityLabel);
        const showEligibilityRefresh = !blankShell;

        return (
            <section
                className="lead-member-hero"
                id="lead-section-demographics-actions"
                aria-label="Member demographics, population of focus, and critical flags"
            >
                <div className="lead-member-hero__top">
                    <div className="lead-member-hero__identity">
                        <button
                            type="button"
                            className="lead-member-hero__back"
                            onClick={this.goBack}
                            aria-label="Back to Member Summary list"
                        >
                            <MemberBackArrow />
                        </button>
                        <MemberAvatar className="lead-member-hero__avatar" />
                        <div className="lead-member-hero__name-block">
                            <h1 className="lead-member-hero__name">{displayName}</h1>
                            {cinText ? (
                                <p className="lead-member-hero__cin">{cinText}</p>
                            ) : null}
                        </div>
                    </div>
                    <div className="lead-member-hero__badges">
                        <span
                            className={`lead-badge lead-badge--member-status lead-tone-${memberStatusBadge.tone}`}
                        >
                            Member Status:{" "}
                            <strong>{memberStatusBadge.label}</strong>
                        </span>
                        <span
                            className={`lead-badge lead-badge--clinical lead-tone-${clinicalTone}`}
                        >
                            Clinical Score:{" "}
                            <strong>
                                {clinicalScore.label || DEMOGRAPHIC_PLACEHOLDER}
                            </strong>
                        </span>
                        <span
                            className={[
                                "lead-badge",
                                "lead-badge--eligibility",
                                `lead-tone-${
                                    isCheckingEligibility ? "grey" : eligibilityTone
                                }`,
                                showEligibilityRefresh ? "" : "lead-badge--eligibility-static",
                                isCheckingEligibility ? "is-checking" : ""
                            ]
                                .filter(Boolean)
                                .join(" ")}
                            aria-busy={isCheckingEligibility ? "true" : undefined}
                        >
                            <span>
                                Eligibility: <strong>{eligibilityLabel}</strong>
                            </span>
                            {showEligibilityRefresh ? (
                                <button
                                    type="button"
                                    className="lead-badge__refresh"
                                    onClick={this.verifyEligibility}
                                    disabled={isCheckingEligibility}
                                    aria-label="Verify eligibility"
                                    title={
                                        isCheckingEligibility
                                            ? "Checking Eligibility"
                                            : "Verify Eligibility"
                                    }
                                >
                                    <LeadRefreshIcon spinning={isCheckingEligibility} />
                                </button>
                            ) : null}
                        </span>
                        <div className="lead-feedback">
                            <span className="lead-feedback__label">Feedback:</span>
                            <button
                                type="button"
                                className="lead-feedback__btn"
                                aria-label="Provide feedback"
                                title="Feedback"
                            >
                                <FeedbackIcon />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="lead-member-hero__demo-row">
                    <div className="lead-member-hero__demo-item">
                        <span className="lead-member-hero__demo-label">Gender</span>
                        <span className="lead-member-hero__demo-value">{gender}</span>
                    </div>
                    <div className="lead-member-hero__demo-item">
                        <span className="lead-member-hero__demo-label">DOB</span>
                        <span className="lead-member-hero__demo-value">
                            {hasDobAge ? (
                                <DobWithAgeDisplay
                                    dob={memberDetails?.dob}
                                    age={memberDetails?.age}
                                    ageStyle="short"
                                />
                            ) : (
                                DEMOGRAPHIC_PLACEHOLDER
                            )}
                        </span>
                    </div>
                    <div className="lead-member-hero__demo-item">
                        <div className="lead-member-hero__demo-label-row">
                            <span className="lead-member-hero__demo-label">Phone</span>
                            {phoneLines.length > 1 ? (
                                <button
                                    type="button"
                                    className="lead-member-hero__view-all"
                                    onClick={this.openPhoneModal}
                                >
                                    View all ({phoneLines.length})
                                </button>
                            ) : null}
                        </div>
                        <span className="lead-member-hero__demo-value lead-member-hero__demo-value--phone">
                            {primaryPhone}
                        </span>
                    </div>
                    <div className="lead-member-hero__demo-item">
                        <span className="lead-member-hero__demo-label">Email</span>
                        <span className="lead-member-hero__demo-value">{email}</span>
                    </div>
                    <div className="lead-member-hero__demo-item">
                        <div className="lead-member-hero__demo-label-row">
                            <span className="lead-member-hero__demo-label">Language</span>
                            {languageEntries.length > 1 ? (
                                <button
                                    type="button"
                                    className="lead-member-hero__view-all"
                                    onClick={this.openLanguageModal}
                                >
                                    View all ({languageEntries.length})
                                </button>
                            ) : null}
                        </div>
                        <span className="lead-member-hero__demo-value">{preferredLanguage}</span>
                    </div>
                    <div className="lead-member-hero__demo-item lead-member-hero__demo-item--address">
                        <div className="lead-member-hero__demo-label-row">
                            <span className="lead-member-hero__demo-label">Address</span>
                            {addressEntries.length > 1 ? (
                                <button
                                    type="button"
                                    className="lead-member-hero__view-all"
                                    onClick={this.openAddressModal}
                                >
                                    View all ({addressEntries.length})
                                </button>
                            ) : null}
                        </div>
                        <span className="lead-member-hero__demo-value lead-member-hero__demo-value--address">
                            {primaryAddress}
                        </span>
                    </div>
                </div>

                <div className="lead-member-hero__divider" aria-hidden="true" />
                <div className="lead-member-hero__pof-row">
                    {this.renderHeroChipSection({
                        label: "Population of Focus",
                        chipList: pofList,
                        blankShell,
                        visibleCount: visiblePofCount,
                        chipsRef: this.setPofChipsRef,
                        measureRef: this.setPofMeasureRef,
                        onOpenModal: this.openPofModal,
                        chipMeasureAttr: "data-pof-measure",
                        moreMeasureAttr: "data-pof-measure-more",
                        measureKey: "pof"
                    })}
                    <div className="lead-member-hero__pof-col-divider" aria-hidden="true" />
                    {this.renderHeroChipSection({
                        label: "Priority Concerns",
                        chipList: criticalFlagsList,
                        blankShell,
                        visibleCount: visibleCriticalFlagsCount,
                        chipsRef: this.setCriticalFlagsChipsRef,
                        measureRef: this.setCriticalFlagsMeasureRef,
                        onOpenModal: this.openCriticalFlagsModal,
                        chipMeasureAttr: "data-critical-flags-measure",
                        moreMeasureAttr: "data-critical-flags-measure-more",
                        measureKey: "critical-flags"
                    })}
                </div>

                {this.renderAddressModal(addressEntries)}
                {this.renderPhoneModal(phoneLines)}
                {this.renderLanguageModal(languageEntries)}
                {this.renderPofModal(pofList)}
                {this.renderCriticalFlagsModal(criticalFlagsList)}
            </section>
        );
    }

    resolveHeroMemberRow(apiData) {
        return (
            getMemberDetailsFromSummary(apiData) ||
            this.state.leadListRow ||
            null
        );
    }

    renderApiSummary(apiData) {
        const cin = apiData.cin_no || getCinFromProps(this.props);
        const memberRow = this.resolveHeroMemberRow(apiData);
        const ls = pickMemberSummaryPayload(apiData, getSummaryKindFromProps(this.props));
        const pofList = normalizePofList(memberRow);
        const criticalFlagsList = normalizeCriticalFlagsList(ls);

        // No real synopsis / Q&A content — same empty state as lead summary.
        if (!leadSummaryHasRenderableBody(ls)) {
            return this.renderCinNotFound();
        }

        return (
            <div className="leads-member-detail-page">
                {this.renderMemberHeroCard(memberRow, cin, pofList, { criticalFlagsList })}
                <LeadSummaryWorkspace
                    leadSummary={ls}
                    lastUpdated={apiData?.last_updated || apiData?.summary_date}
                    onRefresh={this.refreshSummary}
                    isRefreshing={this.state.isRefreshingSummary}
                />
            </div>
        );
    }

    renderCinNotFound() {
        const cin = getCinFromProps(this.props);

        return (
            <div className="leads-member-detail-page">
                {this.renderMemberHeroCard(null, cin, [], {
                    blankShell: true,
                    criticalFlagsList: []
                })}
                <section
                    className="lead-summary-missing lead-summary-missing--unavailable"
                    aria-labelledby="lead-summary-unavailable-title"
                >
                    <div className="lead-summary-missing__content">
                        <p>
                            This Member is not yet queued for Summary Generation.
                        </p>
                    </div>
                </section>
            </div>
        );
    }

    renderMissingSummary() {
        const cin = getCinFromProps(this.props);
        const { isRefreshingSummary, apiData } = this.state;
        const memberRow = this.resolveHeroMemberRow(apiData);
        if (!memberRow) {
            return this.renderCinNotFound();
        }
        const pofList = normalizePofList(memberRow);
        const kind = getSummaryKindFromProps(this.props);
        const ls = pickMemberSummaryPayload(apiData, kind);
        const criticalFlagsList = normalizeCriticalFlagsList(ls);

        return (
            <div className="leads-member-detail-page">
                {this.renderMemberHeroCard(memberRow, cin, pofList, { criticalFlagsList })}
                <section className="lead-summary-missing" aria-labelledby="lead-summary-missing-title">
                    <div className="lead-summary-missing__content">
                       
                        <p>
                            Summary not available yet. Please click “generate now” to generate the
                            summary.
                        </p>
                        <button
                            type="button"
                            className="lead-summary-missing__generate"
                            onClick={this.generateMissingSummary}
                            disabled={isRefreshingSummary}
                        >
                            {isRefreshingSummary ? "Generating..." : "Generate Now"}
                            <LeadRefreshIcon spinning={false} />
                        </button>
                    </div>
                </section>
            </div>
        );
    }

    renderBody() {
        const cin = getCinFromProps(this.props);
        const { isLoading, apiData, error, summaryMissing, cinNotFound } = this.state;

        if (!cin) {
            return (
                <div className="leads-member-detail-page">
                    <h2>Member not found</h2>
                    <p className="leads-member-detail-meta">
                        <button type="button" className="btn btn-link" style={{ paddingLeft: 0 }} onClick={this.goBack}>
                            Back to Members Summary
                        </button>
                    </p>
                </div>
            );
        }

        if (isLoading) {
            return null;
        }

        if (error) {
            return (
                <div className="leads-member-detail-page">
                    <button type="button" className="btn btn-link lead-detail-back" onClick={this.goBack}>
                        <i className="fa fa-angle-left" aria-hidden="true" /> Back
                    </button>
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                    {shouldUseMemberSummaryRemote() ? (
                        <button type="button" className="btn btn-primary btn-sm" onClick={this.loadFromApi}>
                            Retry
                        </button>
                    ) : null}
                </div>
            );
        }

        if (apiData) {
            return this.renderApiSummary(apiData);
        }

        if (cinNotFound || summaryMissing) {
            return this.renderCinNotFound();
        }

        return (
            <div className="leads-member-detail-page">
                <button type="button" className="btn btn-link lead-detail-back" onClick={this.goBack}>
                    <i className="fa fa-angle-left" aria-hidden="true" /> Back
                </button>
                <div className="alert alert-info">No member summary returned for this CIN.</div>
            </div>
        );
    }

    render() {
        const { isRefreshingSummary, apiData, isLoading } = this.state;
        const showPageSpinner = Boolean(isRefreshingSummary || isLoading);
        const summaryBusyMessage = isLoading
            ? "Loading member summary..."
            : apiData
              ? "Updating Summary"
              : "Generating summary...";

        return (
            <div className="page">
                <div className="app-header">
                    <Header2
                        query={this.props.query}
                        settings={this.props.settings}
                        location={this.props.location}
                        urlParams={this.props.urlParams}
                    />
                </div>
                <div className="patient-search-results leads-detail-shell">{this.renderBody()}</div>
                {this.renderToast()}
                {showPageSpinner
                    ? createPortal(
                          <LeadLoadingSpinner page message={summaryBusyMessage} />,
                          document.body
                      )
                    : null}
            </div>
        );
    }
}

export default connect((state) => state)(withRouter(MembersMemberDetail));
